import { useState, useRef, useEffect } from 'react'
import { Search, LogOut, Key, ChevronDown, Shield, Menu } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { COMPANY_PROFILE } from '@/config/companyProfile'
import { useAuth } from '@/features/auth/AuthContext'
import { ChangePasswordModal } from '@/features/auth/ChangePasswordModal'

export interface TopbarProps {
  onToggleMobileNav?: () => void
}

export function Topbar({ onToggleMobileNav }: TopbarProps = {}) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    setDropdownOpen(false)
    await logout()
    navigate('/login')
  }

  const initials = (() => {
    const raw = (user?.displayName || user?.username || '').replace(/[()[\]{}<>,._-]/g, ' ').trim()
    const words = raw.split(/\s+/).filter(Boolean)
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    }
    if (words.length === 1 && words[0]) {
      return words[0].slice(0, 2).toUpperCase()
    }
    return 'MP'
  })()

  const primaryRole = user?.roles?.[0] || 'NHÂN VIÊN'

  return (
    <>
      <header className="flex h-[var(--topbar-height)] shrink-0 items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-2 sm:px-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            type="button"
            onClick={onToggleMobileNav}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg)] text-slate-700 hover:bg-slate-100 md:hidden"
            aria-label="Mở menu điều hướng"
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2 md:hidden shrink-0">
            <span className="text-xs font-bold text-slate-800">MỘC PHÁT</span>
          </div>

          <div className="relative hidden w-full max-w-xs sm:block md:max-w-md">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Tìm kiếm..."
              className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg)] pr-3 pl-8 text-sm outline-none focus:border-[var(--color-accent)] focus:bg-white focus:ring-1 focus:ring-[var(--color-accent)]/20"
              aria-label="Tìm kiếm toàn hệ thống"
              readOnly
            />
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {COMPANY_PROFILE.defaultSiteBanner &&
          !COMPANY_PROFILE.defaultSiteBanner.startsWith('[') ? (
            <span className="hidden rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-muted)] lg:inline">
              {COMPANY_PROFILE.defaultSiteBanner}
            </span>
          ) : null}

          {/* User Menu Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="flex items-center gap-1.5 sm:gap-2.5 rounded-lg p-1 text-left transition hover:bg-slate-100 focus:outline-none"
              aria-label="Menu người dùng"
              aria-expanded={dropdownOpen}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-800 text-xs font-bold text-white shadow-xs">
                {initials}
              </div>
              <div className="hidden leading-tight sm:block">
                <div className="text-sm font-semibold text-[var(--color-text)]">
                  {user?.displayName || user?.username || 'Người dùng'}
                </div>
                <div className="text-xs font-medium text-emerald-800">
                  {primaryRole}
                </div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-xl z-50">
                <div className="border-b border-slate-100 px-3 py-2">
                  <div className="text-xs font-semibold text-slate-800">
                    {user?.displayName || user?.username}
                  </div>
                  <div className="text-xs text-slate-500">@{user?.username}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {user?.roles?.map((r) => (
                      <span
                        key={r}
                        className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-800"
                      >
                        <Shield className="h-2.5 w-2.5" />
                        {r}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => {
                      setDropdownOpen(false)
                      setPasswordModalOpen(true)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Key className="h-3.5 w-3.5 text-slate-400" />
                    <span>Đổi mật khẩu</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50"
                  >
                    <LogOut className="h-3.5 w-3.5 text-rose-500" />
                    <span>Đăng xuất</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </>
  )
}
